import { GridColDef } from "@mui/x-data-grid-pro";
import { IconButton } from "@mui/material";

import ItemStatus from "../../../../../../common/components/item_status";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import dayjs from "dayjs";
import TagStatus from "../../../../../../common/components/tag_status";

/**
 * GenerateColumnsDefinition
 */

type Props = {
  onActionEdit: (idFuncionario: number | null, openState: boolean) => void,
};

export const GenerateColumnsDefinition = ({
  onActionEdit,
}: Props): GridColDef[] => {
  const computeItemStatusTitle = (status: number) => {
    switch (status) {
      case 1:
        return "Ativo";
      case 2:
        return "Demitido";
      case 3:
        return "Afastado";
      case 4:
        return "AP";
      case 5:
        return "APV";
      default:
        return "?";
    }
  };

  const computeItemStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return "green";
      case 2:
        return "red";
      case 3:
        return "yellow";
      case 4:
        return "orange";
      case 5:
        return "darkorange";
      default:
        return "blue";
    }
  };

  const computeItemStatusDescription = (status: number) => {
    switch (status) {
      case 1:
        return "Funcionário ativo";
      case 2:
        return "Funcionário demitido";
      case 3:
        return "Funcionário afastado";
      case 4:
        return "Funcionário em Aviso Prévio";
      case 5:
        return "Funcionário em Aviso Prévio Vencido";
      default:
        return "?";
    }
  };

  const computeItemStatusAvaliacaoHabilidadesTitle = (status: number) => {
    switch (status) {
      case 1:
        return "HA";
      case 2:
        return "HD";
      case 3:
        return "NH";
      case 4:
        return "HC";
      default:
        return "?";
    }
  };

  const computeItemStatusAvaliacaoHabilidadesColor = (status: number) => {
    switch (status) {
      case 1:
        return "red";
      case 2:
        return "yellow";
      case 3:
        return "gray";
      case 4:
        return "green";
      default:
        return "blue";
    }
  };

  const computeItemStatusAvaliacaoHabilidadesDescription = (status: number) => {
    switch (status) {
      case 1:
        return "Habilidades em atraso";
      case 2:
        return "Habilidades a serem realizadas dentro do prazo";
      case 3:
        return "Não possui habilidades";
      case 4:
        return "Habilidades concluídas no prazo";
      default:
        return "?";
    }
  };

  const computeItemStatusAvaliacaoTreinamentosTitle = (status: number) => {
    switch (status) {
      case 1:
        return "AT";
      case 2:
        return "NT";
      case 3:
        return "TNR";
      case 4:
        return "AC";
      default:
        return "?";
    }
  };

  const computeItemStatusAvaliacaoTreinamentosColor = (status: number) => {
    switch (status) {
      case 1:
        return "red";
      case 2:
        return "gray";
      case 3:
        return "yellow";
      case 4:
        return "green";
      default:
        return "blue";
    }
  };

  const computeItemStatusAvaliacaoTreinamentosDescription = (status: number) => {
    switch (status) {
      case 1:
        return "Avaliação de treinamentos em atraso";
      case 2:
        return "Não possui treinamentos";
      case 3:
        return "Treinamentos não realizados";
      case 4:
        return "Avaliação de treinamentos concluídas";
      default:
        return "?";
    }
  };

  const computeItemStatusExperienciasTitle = (status: number) => {
    switch (status) {
      case 1:
        return "EE";
      case 2:
        return "EV";
      case 3:
        return "EA";
      case 4:
        return "ER";
      default:
        return "?";
    }
  };

  const computeItemStatusExperienciasColor = (status: number) => {
    switch (status) {
      case 1:
        return "gray";
      case 2:
        return "yellow";
      case 3:
        return "green";
      case 4:
        return "red";
      default:
        return "blue";
    }
  };

  const computeItemStatusExperienciasDescription = (status: number) => {
    switch (status) {
      case 1:
        return "Em Andamento";
      case 2:
        return "Vencida";
      case 3:
        return "Aprovada";
      case 4:
        return "Reprovada";
      default:
        return "?";
    }
  };

  return [
    {
      field: "status",
      filterable: false,
      headerName: "Status",
      minWidth: 60,
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full h-full flex justify-center items-center">
          <ItemStatus
            textSize="small"
            size="autowidth"
            title={computeItemStatusTitle(params.value)}
            color={computeItemStatusColor(params.value)}
            description={computeItemStatusDescription(params.value)}
          />
        </div>
      )
    },
    {
      field: "statusAvaliacaoHabilidades",
      filterable: false,
      headerName: "Habilidades",
      minWidth: 85,
      width: 85,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full h-full flex justify-center items-center">
          <TagStatus
            color={computeItemStatusAvaliacaoHabilidadesColor(params.value)}
            title={computeItemStatusAvaliacaoHabilidadesTitle(params.value)}
            description={computeItemStatusAvaliacaoHabilidadesDescription(params.value)} />
        </div>
      ),
    },
    {
      field: "statusAvaliacaoTreinamentos",
      filterable: false,
      headerName: "Treinamentos",
      minWidth: 90,
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full h-full flex justify-center items-center">
          <TagStatus
            color={computeItemStatusAvaliacaoTreinamentosColor(params.value)}
            title={computeItemStatusAvaliacaoTreinamentosTitle(params.value)}
            description={computeItemStatusAvaliacaoTreinamentosDescription(params.value)} />
        </div>
      ),
    },
    {
      field: "statusExperiencias",
      filterable: false,
      headerName: "Experiência",
      minWidth: 85,
      width: 85,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full h-full flex justify-center items-center">
          <TagStatus
            color={computeItemStatusExperienciasColor(params.value)}
            title={computeItemStatusExperienciasTitle(params.value)}
            description={computeItemStatusExperienciasDescription(params.value)} />
        </div>
      ),
    },
    {
      field: "nome",
      filterable: false,
      headerName: "Nome",
      flex: 2,
      minWidth: 150,
      sortable: false,
      headerClassName: 'funcionarios-header',
    },
    {
      field: "cargoDescricao",
      filterable: false,
      headerName: "Cargo",
      minWidth: 150,
      flex: 2,
      sortable: false,
      headerClassName: 'funcionarios-header',
    },
    {
      field: "centroCustoDescricao",
      filterable: false,
      headerName: "Centro de Custo",
      minWidth: 150,
      flex: 2,
      sortable: false,
    },
    {
      field: "dataAdmissao",
      filterable: false,
      headerName: "Admissão",
      width: 95,
      minWidth: 95,
      sortable: false,
      renderCell: (params) => {
        return (<span>{params.value && dayjs(params.value).format("DD/MM/YYYY")}</span>);
      },
    },
    {
      field: "dataDemissao",
      filterable: false,
      headerName: "Demissão",
      width: 95,
      minWidth: 95,
      sortable: false,
      renderCell: (params) => {
        return (<span>{params.value && dayjs(params.value).format("DD/MM/YYYY")}</span>);
      },
    },
    {
      field: "dataAfastamento",
      filterable: false,
      headerName: "Afastamento",
      width: 100,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        return (<span>{params.value && dayjs(params.value).format("DD/MM/YYYY")}</span>);
      },
    },
    {
      field: 'idFuncionario',
      sortable: false,
      filterable: false,
      headerName: 'Ações',
      width: 60,
      renderCell: (params) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => onActionEdit(params.value, true)}>
              <DynamicIcon nameIcon="EditRounded" size={25} className="dark:text-white text-primary-green" />
            </IconButton>
          </div>
        );
      }
    }
  ];
}